import { useCallback, useState } from 'react'
import { useLegacyUpdatesStream } from './useLegacyUpdatesStream'
import type { LegacyUpdateEvent } from './useLegacyUpdatesStream'
import type { DriftEvent, ContextEngineEventType, ContextDomain } from '@/types/contextEngine'

interface ContextUpdatesOptions {
  enabled?: boolean
  onDrift?: (event: DriftEvent) => void
}

/**
 * Subscribes to context engine SSE events via the updates stream.
 * Parses context-specific payloads and surfaces drift events for toast notifications.
 */
export function useContextUpdates({ enabled = true, onDrift }: ContextUpdatesOptions = {}) {
  const [connected, setConnected] = useState(false)

  const handleUpdate = useCallback(
    (event: LegacyUpdateEvent) => {
      if (event.entityType !== 'context') return

      const payload = event.payload
      const drift: DriftEvent = {
        domain: (payload.domain as ContextDomain) ?? 'product',
        eventType: (event.eventType as ContextEngineEventType) ?? 'CHUNKS_INDEXED',
        description: (payload.description as string) ?? 'Context updated',
        chunkId: payload.chunkId as string | undefined,
        conflictId: payload.conflictId as string | undefined,
        timestamp: new Date().toISOString(),
      }

      onDrift?.(drift)
    },
    [onDrift],
  )

  const handleConnected = useCallback(() => {
    setConnected(true)
  }, [])

  const { disconnect } = useLegacyUpdatesStream({
    entityTypes: ['context'],
    enabled,
    onUpdate: handleUpdate,
    onConnected: handleConnected,
  })

  return { connected, disconnect }
}
